import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { formatDate } from "@/lib/utils";

const CARD_BORDER = "hsl(210,30%,85%)";

const COL_LABELS: Record<string, string> = {
  name: "Name",
  code: "Code",
  title: "Title",
  type: "Type",
  status: "Status",
  priority: "Priority",
  source: "Source",
  progress: "Progress",
  dueDate: "Due Date",
  startDate: "Start Date",
  endDate: "End Date",
  assignee: "Assignee",
  organizer: "Organizer",
  productName: "Product",
  projectName: "Project",
  meetingTitle: "Meeting",
  entityContext: "Entity",
  scheduledAt: "Date",
  createdBy: "Created By",
  createdAt: "Created At",
  decidedBy: "Decided By",
  decidedAt: "Decided At",
  projectCount: "Projects",
  amount: "Amount",
  category: "Category",
  expenseDate: "Date",
  submittedBy: "Submitted By",
};

const DATE_COLUMNS = new Set([
  "dueDate",
  "scheduledAt",
  "createdAt",
  "decidedAt",
  "startDate",
  "endDate",
  "expenseDate",
]);

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50] as const;
const DEFAULT_PAGE_SIZE = 10;

/**
 * Pill matching CompanyDetailPage's old local StatusBadge — Active /
 * Inactive when value is ACTIVE/INACTIVE, otherwise a neutral pill
 * displaying the raw status (covers PLANNING / IN_PROGRESS / DONE etc.
 * from the Project & Task statuses).
 */
function StatusBadge({ status }: { status: string }) {
  const value = (status ?? "").toUpperCase();
  if (value === "ACTIVE") {
    return (
      <span
        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
        style={{ background: "hsl(142,55%,92%)", color: "hsl(142,72%,28%)" }}
      >
        Active
      </span>
    );
  }
  if (value === "INACTIVE") {
    return (
      <span
        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
        style={{ background: "hsl(210,16%,93%)", color: "hsl(215,16%,40%)" }}
      >
        Inactive
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium"
      style={{ background: "hsl(210,55%,92%)", color: "hsl(210,100%,28%)" }}
    >
      {status || "—"}
    </span>
  );
}

export type EntityTableTabRow = Record<string, unknown> & { id?: string };

type Props = {
  data: EntityTableTabRow[];
  isLoading: boolean;
  columns: string[];
  emptyText: string;
  /**
   * Called when the user clicks a row. Pass `undefined` (or a function
   * that returns nothing for the row) to make rows non-clickable —
   * respects RLAC by letting callers gate per-row whether navigation
   * should happen.
   */
  onRowClick?: (row: EntityTableTabRow) => void;
};

/**
 * Generic entity table used inside detail-page tabs (Products under a
 * Company, Tasks under a Project, etc.). Provides Tasks-list-style
 * pagination (page-size selector + page nav) so every tab scales past
 * the default fetch limit without becoming a wall of rows.
 *
 * Pagination is client-side over the data array — sub-entity queries
 * already fetch the full page (limit 50) up front. If a tab needs
 * cursor-based pagination later, the pagination state can be lifted.
 */
export function EntityTableTab({
  data,
  isLoading,
  columns,
  emptyText,
  onRowClick,
}: Props) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  const total = data.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  // Clamp page when data shrinks (e.g. after deletes) so we never end
  // up showing an empty page beyond the data range.
  const safePage = Math.min(page, totalPages);

  const paged = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return data.slice(start, start + pageSize);
  }, [data, safePage, pageSize]);

  const startIndex = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endIndex = Math.min(total, safePage * pageSize);

  return (
    <div
      className="overflow-hidden rounded-xl border bg-card"
      style={{ borderColor: CARD_BORDER }}
    >
      {isLoading ? (
        <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
          Loading…
        </div>
      ) : total === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          {emptyText}
        </div>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="w-12 px-6 py-3">#</th>
                {columns.map((c) => (
                  <th key={c} className="px-6 py-3">
                    {COL_LABELS[c] ?? c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {paged.map((row, i) => {
                const rowKey = (row.id as string | undefined) ?? `${safePage}-${i}`;
                const clickable = !!onRowClick;
                return (
                  <tr
                    key={rowKey}
                    onClick={clickable ? () => onRowClick!(row) : undefined}
                    className={`transition-colors hover:bg-[hsl(210,50%,98%)] ${
                      clickable ? "cursor-pointer" : ""
                    }`}
                  >
                    <td className="px-6 py-3 text-muted-foreground">
                      {(safePage - 1) * pageSize + i + 1}
                    </td>
                    {columns.map((c) => {
                      const v = row[c];
                      return (
                        <td key={c} className="px-6 py-3">
                          {c === "progress" ? (
                            <div className="flex items-center gap-2">
                              <div className="h-1.5 w-16 rounded-full bg-[hsl(210,16%,93%)]">
                                <div
                                  className="h-full rounded-full bg-primary"
                                  style={{
                                    width: `${(v as number) ?? 0}%`,
                                  }}
                                />
                              </div>
                              <span className="text-xs text-muted-foreground">
                                {(v as number) ?? 0}%
                              </span>
                            </div>
                          ) : c === "amount" ? (
                            <span className="font-medium">
                              $
                              {(
                                (v as number | null | undefined) ?? 0
                              ).toLocaleString()}
                            </span>
                          ) : DATE_COLUMNS.has(c) ? (
                            formatDate((v as string | null | undefined) ?? null)
                          ) : c === "status" ? (
                            <StatusBadge
                              status={(v as string | undefined) ?? "INACTIVE"}
                            />
                          ) : (
                            ((v as string | null | undefined) ?? "—") || "—"
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div
            className="flex items-center justify-between border-t bg-[hsl(210,30%,97%)] px-[14px] py-[9px] text-xs text-muted-foreground"
            style={{ borderColor: CARD_BORDER }}
          >
            <div className="flex items-center gap-3">
              <span>
                Showing {startIndex}-{endIndex} of {total} entries
              </span>
              <span className="flex items-center gap-1.5">
                Show
                <Select
                  value={String(pageSize)}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-7 w-[68px] text-xs"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
                entries
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" strokeWidth={1.5} />
              </Button>
              <span className="px-2 text-foreground">
                Page {safePage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="icon"
                disabled={safePage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                <ChevronRight className="h-4 w-4" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
